import { useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { cn } from '@/lib/utils';
import { Plus, RefreshCw, Search, GraduationCap, Users, Video, Youtube, MessageSquareText, Handshake } from 'lucide-react';
import type { CreatorStatusFilter, VideoStatusFilter } from './types';
import {
    useAcademyStats,
    useAcademyCreators,
    useAcademyVideos,
    useUpdateCreatorStatus,
    useUpdateVideoStatus,
    useUpdateVideo,
    useDeleteVideo,
} from './hooks/useAcademyAdmin';
import {
    AcademyStatsPanel,
    AcademyCreatorWithVideos,
    AcademyVideoCard,
    AcademyRejectDialog,
    AcademyDeleteVideoDialog,
    AcademyCreateVideoForm,
    AcademyCreateCreatorForm,
    AcademyYouTubeImportTab,
    AcademyVideoEditCategoriesModal,
    AcademyRatingFeedbackPanel,
    AcademyExpertAdvisoriesPanel,
} from './components';
import type { AcademyCreatorItem, AcademyVideoItem } from './types';

export function AcademyPage() {
    const [activeTab, setActiveTab] = useState<'creators' | 'videos' | 'import' | 'ratings' | 'advisories'>('creators');
    const [importPreselectedCreatorId, setImportPreselectedCreatorId] = useState<string | null>(null);
    const [creatorStatusFilter, setCreatorStatusFilter] = useState<CreatorStatusFilter>('all');
    const [videoStatusFilter, setVideoStatusFilter] = useState<VideoStatusFilter>('all');
    const [creatorSearch, setCreatorSearch] = useState('');
    const [videoSearch, setVideoSearch] = useState('');
    const [expandedCreatorId, setExpandedCreatorId] = useState<string | null>(null);
    const [expandedVideoId, setExpandedVideoId] = useState<string | null>(null);
    const [showCreateVideoForm, setShowCreateVideoForm] = useState(false);
    const [showCreateCreatorForm, setShowCreateCreatorForm] = useState(false);
    const [videoCreatorFilter, setVideoCreatorFilter] = useState<string | null>(null);
    const [rejectTarget, setRejectTarget] = useState<{ type: 'creator' | 'video'; item: AcademyCreatorItem | AcademyVideoItem } | null>(null);
    const [deleteVideoTarget, setDeleteVideoTarget] = useState<AcademyVideoItem | null>(null);
    const [editCategoriesVideo, setEditCategoriesVideo] = useState<AcademyVideoItem | null>(null);
    const [importResultMessage, setImportResultMessage] = useState<string | null>(null);
    const [importResultIsError, setImportResultIsError] = useState(false);

    const { data: stats } = useAcademyStats();
    const { data: creators, isLoading: creatorsLoading, refetch: refetchCreators } = useAcademyCreators(creatorStatusFilter, creatorSearch);
    const {
        data: importCreators,
        isLoading: importCreatorsLoading,
        refetch: refetchImportCreators,
    } = useAcademyCreators('all', '');
    const { data: videos, isLoading: videosLoading, refetch: refetchVideos } = useAcademyVideos(videoStatusFilter, videoCreatorFilter, videoSearch);

    const statusCreatorMutation = useUpdateCreatorStatus();
    const statusVideoMutation = useUpdateVideoStatus();
    const updateVideoMutation = useUpdateVideo();
    const deleteVideoMutation = useDeleteVideo();

    const CREATOR_STATUS_TABS: { value: CreatorStatusFilter; label: string; count?: number }[] = [
        { value: 'all', label: 'Todos', count: stats?.total_creators },
        { value: 'approved', label: 'Aprobados', count: stats?.creators_approved },
        { value: 'pending', label: 'Pendientes', count: stats?.creators_pending },
        { value: 'rejected', label: 'Rechazados', count: stats?.creators_rejected },
    ];

    const VIDEO_STATUS_TABS: { value: VideoStatusFilter; label: string; count?: number }[] = [
        { value: 'all', label: 'Todos', count: stats?.total_videos },
        { value: 'approved', label: 'Aprobados', count: stats?.videos_approved },
        { value: 'pending', label: 'Pendientes', count: stats?.videos_pending },
        { value: 'rejected', label: 'Rechazados', count: stats?.videos_rejected },
    ];

    const handleRejectConfirm = (reason: string) => {
        if (!rejectTarget) return;
        if (rejectTarget.type === 'creator') {
            statusCreatorMutation.mutate({
                creatorId: (rejectTarget.item as AcademyCreatorItem).id,
                newStatus: 'rejected',
                rejectionReason: reason,
            });
        } else {
            statusVideoMutation.mutate({
                videoId: (rejectTarget.item as AcademyVideoItem).id,
                newStatus: 'rejected',
                rejectionReason: reason,
            });
        }
        setRejectTarget(null);
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Academia de Pesca"
                description="Gestión de creadores de contenido y vídeos"
            >
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => { setShowCreateCreatorForm(!showCreateCreatorForm); setShowCreateVideoForm(false); }}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            showCreateCreatorForm
                                ? 'bg-muted text-muted-foreground hover:bg-muted/80'
                                : 'bg-muted/50 text-foreground hover:bg-muted'
                        )}
                    >
                        <Plus size={14} />
                        {showCreateCreatorForm ? 'Cancelar' : 'Nuevo Creador'}
                    </button>
                    <button
                        onClick={() => { setShowCreateVideoForm(!showCreateVideoForm); setShowCreateCreatorForm(false); }}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            showCreateVideoForm
                                ? 'bg-muted text-muted-foreground hover:bg-muted/80'
                                : 'bg-primary text-primary-foreground bg-primary/90 shadow-sm'
                        )}
                    >
                        <Plus size={14} />
                        {showCreateVideoForm ? 'Cancelar' : 'Nuevo Vídeo'}
                    </button>
                    <button
                        onClick={() => { refetchCreators(); refetchImportCreators(); refetchVideos(); }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                    >
                        <RefreshCw size={14} />
                        Actualizar
                    </button>
                </div>
            </PageHeader>

            {showCreateCreatorForm && (
                <AcademyCreateCreatorForm
                    onCreated={() => { setShowCreateCreatorForm(false); refetchCreators(); }}
                    onCancel={() => setShowCreateCreatorForm(false)}
                />
            )}
            {showCreateVideoForm && (
                <AcademyCreateVideoForm
                    creators={creators || []}
                    onCreated={() => { setShowCreateVideoForm(false); refetchVideos(); }}
                    onCancel={() => setShowCreateVideoForm(false)}
                />
            )}

            <AcademyStatsPanel stats={stats} />

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                <button
                    onClick={() => setActiveTab('creators')}
                    className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                        activeTab === 'creators'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    <Users size={16} />
                    Creadores
                </button>
                <button
                        onClick={() => setActiveTab('videos')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'videos'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Video size={16} />
                        Vídeos
                    </button>
                <button
                        onClick={() => { setActiveTab('import'); setImportPreselectedCreatorId(null); }}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'import'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Youtube size={16} />
                        Importar YouTube
                    </button>
                <button
                        onClick={() => setActiveTab('ratings')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'ratings'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <MessageSquareText size={16} />
                        Valoraciones
                    </button>
                <button
                        onClick={() => setActiveTab('advisories')}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                            activeTab === 'advisories'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Handshake size={16} />
                        Asesoramientos
                    </button>
            </div>

            {activeTab === 'creators' && (
                <>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                            {CREATOR_STATUS_TABS.map(t => (
                                <button
                                    key={t.value}
                                    onClick={() => setCreatorStatusFilter(t.value)}
                                    className={cn(
                                        'px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                                        creatorStatusFilter === t.value
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {t.label}
                                    {t.count !== undefined && t.count > 0 && (
                                        <span className="ml-1.5 text-[10px] opacity-70">({t.count})</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="relative flex-1 min-w-0 max-w-sm">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Buscar creador..."
                                value={creatorSearch}
                                onChange={e => setCreatorSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
                            />
                        </div>
                    </div>

                    {creatorsLoading ? (
                        <div className="space-y-3">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="h-20 rounded-xl border border-border bg-card animate-pulse" />
                            ))}
                        </div>
                    ) : !creators || creators.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                            <GraduationCap size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                            <p className="text-sm text-muted-foreground">
                                No hay creadores {creatorStatusFilter !== 'all' ? `con estado "${creatorStatusFilter}"` : ''}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {creators.map(c => (
                                <AcademyCreatorWithVideos
                                    key={c.id}
                                    creator={c}
                                    isExpanded={expandedCreatorId === c.id}
                                    videoStatusFilter={videoStatusFilter}
                                    onToggle={() => setExpandedCreatorId(expandedCreatorId === c.id ? null : c.id)}
                                    onApproveCreator={() => statusCreatorMutation.mutate({ creatorId: c.id, newStatus: 'approved' })}
                                    onRejectCreator={() => setRejectTarget({ type: 'creator', item: c })}
                                    onApproveVideo={(v) => statusVideoMutation.mutate({ videoId: v.id, newStatus: 'approved' })}
                                    onRejectVideo={(v) => setRejectTarget({ type: 'video', item: v })}
                                    onDeleteVideo={(v) => setDeleteVideoTarget(v)}
                                    onToggleVideoHidden={(v) => updateVideoMutation.mutate({ p_video_id: v.id, p_hidden: !v.hidden })}
                                    onEditCategories={(v) => setEditCategoriesVideo(v)}
                                    onGoToImport={(c) => { setActiveTab('import'); setImportPreselectedCreatorId(c.id); }}
                                    isActioning={statusCreatorMutation.isPending || statusVideoMutation.isPending || deleteVideoMutation.isPending || updateVideoMutation.isPending}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {activeTab === 'videos' && (
                <>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                            {VIDEO_STATUS_TABS.map(t => (
                                <button
                                    key={t.value}
                                    onClick={() => setVideoStatusFilter(t.value)}
                                    className={cn(
                                        'px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                                        videoStatusFilter === t.value
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {t.label}
                                    {t.count !== undefined && t.count > 0 && (
                                        <span className="ml-1.5 text-[10px] opacity-70">({t.count})</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        <select
                            value={videoCreatorFilter ?? ''}
                            onChange={e => setVideoCreatorFilter(e.target.value || null)}
                            className="px-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 min-w-[180px]"
                        >
                            <option value="">Todos los creadores</option>
                            {(creators || []).map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.channel_display_name || c.user_display_name || c.user_email || c.youtube_channel_url}
                                </option>
                            ))}
                        </select>
                        <div className="relative flex-1 min-w-0 max-w-sm">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Buscar vídeo..."
                                value={videoSearch}
                                onChange={e => setVideoSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
                            />
                        </div>
                    </div>

                    {videosLoading ? (
                        <div className="space-y-3">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="h-20 rounded-xl border border-border bg-card animate-pulse" />
                            ))}
                        </div>
                    ) : !videos || videos.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                            <Video size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                            <p className="text-sm text-muted-foreground">
                                No hay vídeos {videoStatusFilter !== 'all' ? `con estado "${videoStatusFilter}"` : ''}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {videos.map(v => (
                                <AcademyVideoCard
                                    key={v.id}
                                    video={v}
                                    isExpanded={expandedVideoId === v.id}
                                    onToggle={() => setExpandedVideoId(expandedVideoId === v.id ? null : v.id)}
                                    onApprove={() => statusVideoMutation.mutate({ videoId: v.id, newStatus: 'approved' })}
                                    onReject={() => setRejectTarget({ type: 'video', item: v })}
                                    onDelete={() => setDeleteVideoTarget(v)}
                                    onToggleHidden={() => updateVideoMutation.mutate({ p_video_id: v.id, p_hidden: !v.hidden })}
                                    onEditCategories={() => setEditCategoriesVideo(v)}
                                    isActioning={statusVideoMutation.isPending || deleteVideoMutation.isPending || updateVideoMutation.isPending}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {activeTab === 'import' && (
                <AcademyYouTubeImportTab
                    creators={importCreators || []}
                    creatorsLoading={importCreatorsLoading}
                    preselectedCreatorId={importPreselectedCreatorId}
                    onCreatorCreated={() => { refetchCreators(); refetchImportCreators(); }}
                    onImportSuccess={(r) => {
                        refetchVideos();
                        refetchCreators();
                        refetchImportCreators();
                        setImportResultIsError(false);
                        const msg = r.inserted > 0
                            ? `${r.inserted} vídeos importados${r.skipped > 0 ? `, ${r.skipped} omitidos (ya existían)` : ''}`
                            : r.skipped > 0
                                ? `${r.skipped} vídeos ya existían`
                                : 'No hay vídeos nuevos para importar';
                        setImportResultMessage(msg);
                    }}
                    onImportError={(message) => {
                        setImportResultIsError(true);
                        setImportResultMessage(message);
                    }}
                />
            )}

            {activeTab === 'ratings' && <AcademyRatingFeedbackPanel />}
            {activeTab === 'advisories' && <AcademyExpertAdvisoriesPanel />}

            <AdminConfirmDialog
                open={!!importResultMessage}
                intent="alert"
                variant={importResultIsError ? 'danger' : 'default'}
                title={importResultIsError ? 'Error en la importación' : 'Importación'}
                description={importResultMessage}
                onCancel={() => {
                    setImportResultMessage(null);
                    setImportResultIsError(false);
                }}
                onConfirm={() => {
                    setImportResultMessage(null);
                    setImportResultIsError(false);
                }}
            />

            <AcademyRejectDialog
                isOpen={!!rejectTarget}
                title={rejectTarget ? ((rejectTarget.item as AcademyVideoItem).title ?? (rejectTarget.item as AcademyCreatorItem).user_display_name ?? (rejectTarget.item as AcademyCreatorItem).youtube_channel_url ?? 'Item') : ''}
                type={rejectTarget?.type ?? 'creator'}
                onConfirm={handleRejectConfirm}
                onCancel={() => setRejectTarget(null)}
            />

            <AcademyDeleteVideoDialog
                isOpen={!!deleteVideoTarget}
                video={deleteVideoTarget}
                isDeleting={deleteVideoMutation.isPending}
                onConfirm={() => {
                    if (deleteVideoTarget) {
                        deleteVideoMutation.mutate(deleteVideoTarget.id, {
                            onSuccess: () => setDeleteVideoTarget(null),
                        });
                    }
                }}
                onCancel={() => setDeleteVideoTarget(null)}
            />

            <AcademyVideoEditCategoriesModal
                video={editCategoriesVideo}
                isOpen={!!editCategoriesVideo}
                onClose={() => setEditCategoriesVideo(null)}
                onSave={async (videoId, values) => {
                    await updateVideoMutation.mutateAsync({
                        p_video_id: videoId,
                        p_modalities: values.modalities,
                        p_species: values.species,
                        p_location_types: values.location_types,
                        p_water_type: values.water_type || null,
                        p_content_type: values.content_type || null,
                        p_language_code: values.language_code || null,
                        p_level: values.level || null,
                    });
                }}
                isSaving={updateVideoMutation.isPending}
            />
        </div>
    );
}
